import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, Loader2, Plus, Trash2 } from 'lucide-react';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { DatePicker } from '@/components/ui/date-picker';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useDestinationsList } from '@/features/masters/destinations/useDestinations';
import { useMaterialsList } from '@/features/masters/materials/useMaterials';
import { useWarehousesList } from '@/features/masters/warehouses/useWarehouses';
import { getSortingProcessingState } from '@/features/operations/sorting/types';
import { useSortingPackages } from '@/features/operations/sorting/useSorting';

import { useCreateOutbound, useUpdateOutbound } from './useOutbounds';
import type {
  OutboundDraftInput,
  OutboundDto,
  OutboundLineInput,
  OutboundSourceKind,
} from './types';

interface DraftLine extends OutboundLineInput {
  client_id: string;
}

let nextClientLineId = 1;

const emptyLine = (): DraftLine => ({
  client_id: `outbound-line-${nextClientLineId++}`,
  source_kind: 'material_inventory',
  source_record_id: null,
  material_id: null,
  warehouse_id: '',
  kg: '',
});

const today = () => new Date().toISOString().slice(0, 10);

const linesFromOutbound = (outbound: OutboundDto | null): DraftLine[] =>
  outbound?.lines.length
    ? outbound.lines.map((line) => ({
        client_id: line.outbound_line_id,
        source_kind: line.source_kind,
        source_record_id: line.source_record_id,
        material_id: line.material_id,
        warehouse_id: line.warehouse_id,
        kg: line.kg,
      }))
    : [emptyLine()];

interface OutboundDraftDialogProps {
  batchId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  outbound?: OutboundDto | null;
}

export const OutboundDraftDialog = ({
  batchId,
  open,
  onOpenChange,
  outbound = null,
}: OutboundDraftDialogProps) => {
  const { t } = useTranslation('sales');
  const create = useCreateOutbound(batchId);
  const update = useUpdateOutbound();
  const isEdit = !!outbound;

  const destinationsQuery = useDestinationsList({ status: 'approved', per_page: 200 });
  const destinations = useMemo(
    () => destinationsQuery.data?.rows ?? [],
    [destinationsQuery.data?.rows],
  );
  const materialsQuery = useMaterialsList({ status: 'approved', per_page: 200 });
  const materials = materialsQuery.data?.rows ?? [];
  const warehousesQuery = useWarehousesList({ active_only: true, per_page: 200 });
  const warehouses = warehousesQuery.data?.rows ?? [];
  const packagesQuery = useSortingPackages({
    batch_id: batchId,
    route_type: 'material_sales',
    processing_state: 'available',
    per_page: 500,
  });
  const packages = useMemo(
    () =>
      (packagesQuery.data?.data ?? []).filter(
        (candidate) => getSortingProcessingState(candidate) === 'available',
      ),
    [packagesQuery.data?.data],
  );

  const [outboundCode, setOutboundCode] = useState('');
  const [plannedDispatchDate, setPlannedDispatchDate] = useState(today);
  const [destinationCompanyId, setDestinationCompanyId] = useState('');
  const [lines, setLines] = useState<DraftLine[]>([emptyLine()]);
  const [changeReason, setChangeReason] = useState('');

  useEffect(() => {
    if (!open) return;
    setOutboundCode(outbound?.outbound_code ?? '');
    setPlannedDispatchDate(outbound?.planned_dispatch_date ?? today());
    setDestinationCompanyId(outbound?.destination_company_id ?? '');
    setLines(linesFromOutbound(outbound));
    setChangeReason('');
    create.reset();
    update.reset();
    // Mutation objects are intentionally excluded; their identity changes per render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, outbound]);

  const updateLine = (clientId: string, patch: Partial<DraftLine>) => {
    setLines((current) =>
      current.map((line) => (line.client_id === clientId ? { ...line, ...patch } : line)),
    );
  };

  const selectedDestination = useMemo(
    () =>
      destinations.find(
        (destination) => destination.destination_company_id === destinationCompanyId,
      ),
    [destinationCompanyId, destinations],
  );

  const canSubmit =
    destinationCompanyId !== '' &&
    plannedDispatchDate !== '' &&
    (!isEdit || changeReason.trim() !== '') &&
    lines.length > 0 &&
    lines.every((line) => {
      const kg = Number.parseFloat(line.kg);
      if (!Number.isFinite(kg) || kg <= 0 || !line.warehouse_id) return false;
      if (line.source_kind === 'material_inventory') return !!line.material_id;
      return !!line.source_record_id;
    });

  const submit = async () => {
    const body: OutboundDraftInput = {
      outbound_code: outboundCode.trim() || null,
      planned_dispatch_date: plannedDispatchDate,
      destination_company_id: destinationCompanyId,
      lines: lines.map(({ client_id: _clientId, ...line }) => ({
        ...line,
        source_record_id: line.source_kind === 'inbound_package' ? line.source_record_id : null,
        material_id: line.source_kind === 'material_inventory' ? line.material_id : null,
      })),
    };

    if (outbound) {
      await update.mutateAsync({
        id: outbound.outbound_id,
        body: {
          ...body,
          version: outbound.version,
          change_reason: changeReason.trim(),
        },
      });
    } else {
      await create.mutateAsync(body);
    }
    onOpenChange(false);
  };

  const error = create.error ?? update.error;
  const pending = create.isPending || update.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-5xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {isEdit
              ? t('efs.sales.outbounds.draft.edit_title', { defaultValue: '出荷・販売草稿を編集' })
              : t('efs.sales.outbounds.draft.create_title', {
                  defaultValue: '出荷・販売草稿を作成',
                })}
          </DialogTitle>
          <DialogDescription>
            {t('efs.sales.outbounds.draft.subtitle', {
              defaultValue:
                '一般買方と二次処理業者は同じ出荷フローです。草稿中は在庫を引き落としません。',
            })}
          </DialogDescription>
        </DialogHeader>

        {error ? (
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>{error.message}</AlertDescription>
          </Alert>
        ) : null}

        <div className="grid gap-3 sm:grid-cols-3">
          <div className="space-y-1">
            <label className="text-sm font-medium" htmlFor="outbound-code">
              {t('efs.sales.outbounds.fields.outbound_code', { defaultValue: '出荷コード' })}
            </label>
            <Input
              id="outbound-code"
              value={outboundCode}
              onChange={(event) => setOutboundCode(event.target.value)}
              placeholder="OUT-..."
            />
          </div>
          <div className="space-y-1">
            <label className="text-sm font-medium" htmlFor="planned-dispatch-date">
              {t('efs.sales.outbounds.fields.planned_dispatch_date', {
                defaultValue: '出荷予定日',
              })}
            </label>
            <DatePicker
              id="planned-dispatch-date"
              value={plannedDispatchDate}
              onChange={setPlannedDispatchDate}
            />
          </div>
          <div className="space-y-1">
            <label className="text-sm font-medium" htmlFor="outbound-destination">
              {t('efs.sales.outbounds.fields.destination', { defaultValue: '出荷先' })}
            </label>
            <Select value={destinationCompanyId} onValueChange={setDestinationCompanyId}>
              <SelectTrigger id="outbound-destination">
                <SelectValue placeholder="—" />
              </SelectTrigger>
              <SelectContent>
                {destinations.map((destination) => (
                  <SelectItem
                    key={destination.destination_company_id}
                    value={destination.destination_company_id}
                  >
                    {destination.company_display_name}
                    {' · '}
                    {t(`efs.sales.outbounds.destination_type.${destination.destination_type}`, {
                      defaultValue: destination.destination_type,
                    })}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {selectedDestination ? (
              <p className="text-xs text-efs-text-muted">
                {t(`efs.sales.outbounds.destination_type.${selectedDestination.destination_type}`, {
                  defaultValue: selectedDestination.destination_type,
                })}
              </p>
            ) : null}
          </div>
        </div>

        {isEdit ? (
          <div className="space-y-1">
            <label className="text-sm font-medium" htmlFor="outbound-change-reason">
              {t('efs.sales.outbounds.fields.change_reason', {
                defaultValue: '修正理由',
              })}
            </label>
            <Textarea
              id="outbound-change-reason"
              value={changeReason}
              onChange={(event) => setChangeReason(event.target.value)}
              placeholder={t('efs.sales.outbounds.draft.change_reason_placeholder', {
                defaultValue: '何を、なぜ変更するかを入力してください。',
              })}
              rows={2}
            />
          </div>
        ) : null}

        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold">
              {t('efs.sales.outbounds.fields.lines', { defaultValue: '出荷明細' })}
            </h3>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => setLines((current) => [...current, emptyLine()])}
            >
              <Plus className="mr-1 h-4 w-4" />
              {t('efs.sales.outbounds.actions.add_line', { defaultValue: '明細を追加' })}
            </Button>
          </div>

          {lines.map((line, index) => (
            <div
              key={line.client_id}
              className="grid gap-3 rounded-md border border-efs-border p-3 sm:grid-cols-12"
            >
              <div className="space-y-1 sm:col-span-2">
                <label className="text-xs font-medium" htmlFor={`${line.client_id}-source`}>
                  {t('efs.sales.outbounds.fields.source_kind', { defaultValue: '出荷元' })}
                </label>
                <Select
                  value={line.source_kind}
                  onValueChange={(value: OutboundSourceKind) =>
                    updateLine(line.client_id, {
                      source_kind: value,
                      source_record_id: null,
                      material_id: null,
                    })
                  }
                >
                  <SelectTrigger id={`${line.client_id}-source`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="material_inventory">
                      {t('efs.sales.outbounds.source_kind.material_inventory', {
                        defaultValue: '素材在庫',
                      })}
                    </SelectItem>
                    <SelectItem value="inbound_package">
                      {t('efs.sales.outbounds.source_kind.inbound_package', {
                        defaultValue: '未解体入荷品',
                      })}
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1 sm:col-span-4">
                <label className="text-xs font-medium" htmlFor={`${line.client_id}-item`}>
                  {line.source_kind === 'material_inventory'
                    ? t('efs.sales.outbounds.fields.material', { defaultValue: '素材' })
                    : t('efs.sales.outbounds.fields.package', { defaultValue: '入荷品' })}
                </label>
                {line.source_kind === 'material_inventory' ? (
                  <Select
                    value={line.material_id ?? ''}
                    onValueChange={(value) => updateLine(line.client_id, { material_id: value })}
                  >
                    <SelectTrigger id={`${line.client_id}-item`}>
                      <SelectValue placeholder="—" />
                    </SelectTrigger>
                    <SelectContent>
                      {materials.map((material) => (
                        <SelectItem key={material.material_id} value={material.material_id}>
                          {material.japanese_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <Select
                    value={line.source_record_id ?? ''}
                    onValueChange={(value) => {
                      const selected = packages.find(
                        (candidate) => candidate.inbound_package_id === value,
                      );
                      updateLine(line.client_id, {
                        source_record_id: value,
                        warehouse_id: selected?.warehouse_id ?? line.warehouse_id,
                      });
                    }}
                  >
                    <SelectTrigger id={`${line.client_id}-item`}>
                      <SelectValue placeholder="—" />
                    </SelectTrigger>
                    <SelectContent>
                      {packages.map((candidate) => (
                        <SelectItem
                          key={candidate.inbound_package_id}
                          value={candidate.inbound_package_id}
                        >
                          {candidate.package_code} · {candidate.incoming_item_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>

              <div className="space-y-1 sm:col-span-3">
                <label className="text-xs font-medium" htmlFor={`${line.client_id}-warehouse`}>
                  {t('efs.sales.outbounds.fields.warehouse', { defaultValue: '倉庫' })}
                </label>
                <Select
                  value={line.warehouse_id}
                  onValueChange={(value) => updateLine(line.client_id, { warehouse_id: value })}
                >
                  <SelectTrigger id={`${line.client_id}-warehouse`}>
                    <SelectValue placeholder="—" />
                  </SelectTrigger>
                  <SelectContent>
                    {warehouses.map((warehouse) => (
                      <SelectItem key={warehouse.warehouse_id} value={warehouse.warehouse_id}>
                        {warehouse.code} · {warehouse.display_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1 sm:col-span-2">
                <label className="text-xs font-medium" htmlFor={`${line.client_id}-kg`}>
                  {t('efs.sales.outbounds.fields.kg', { defaultValue: '重量 (kg)' })}
                </label>
                <Input
                  id={`${line.client_id}-kg`}
                  inputMode="decimal"
                  value={line.kg}
                  onChange={(event) => updateLine(line.client_id, { kg: event.target.value })}
                  placeholder="0.000"
                />
              </div>

              <div className="flex items-end justify-end sm:col-span-1">
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  aria-label={t('efs.sales.outbounds.actions.remove_line', {
                    defaultValue: `明細 ${index + 1} を削除`,
                    index: index + 1,
                  })}
                  disabled={lines.length === 1}
                  onClick={() =>
                    setLines((current) =>
                      current.filter((candidate) => candidate.client_id !== line.client_id),
                    )
                  }
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            {t('efs.common.actions.cancel', { ns: 'common', defaultValue: 'キャンセル' })}
          </Button>
          <Button type="button" disabled={!canSubmit || pending} onClick={submit}>
            {pending ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : null}
            {isEdit
              ? t('efs.common.actions.save', { ns: 'common', defaultValue: '保存' })
              : t('efs.sales.outbounds.actions.create', { defaultValue: '作成' })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
